import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
}

const variants: Record<Variant, string> = {
  primary: 'btn-shine bg-cta text-white shadow-glow hover:-translate-y-px hover:brightness-105 hover:shadow-[0_12px_24px_rgb(0_133_119/0.35)] active:translate-y-0 active:brightness-95',
  secondary: 'bg-solid text-white hover:brightness-110 active:brightness-95',
  outline: 'border border-line bg-paper text-navy hover:border-turquoise/50 hover:bg-fill active:bg-primary-light',
  ghost: 'text-blue hover:bg-fill active:bg-primary-light',
  danger: 'bg-danger text-white hover:brightness-95 active:brightness-90',
};
const sizes: Record<Size, string> = {
  sm: 'h-10 px-4 text-sm rounded-md',
  md: 'h-12 px-6 text-[15px] rounded-md',
  lg: 'h-[54px] px-6 text-base rounded-[16px]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  fullWidth,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-bold transition duration-150 select-none active:scale-[0.97]',
        'disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}
